import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  defaultPrinter,
  listPrinters,
  openDrawer,
  previewReceipt,
  printReceipt,
  probeAgent,
  type AgentInfo,
  type PrinterConfig,
  type PrinterKind,
} from '../lib/agent'
import { kvGet, kvSet, type Order } from '../lib/db'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'

/** Hoá đơn giả để in thử — đủ các trường hay hỏng: dấu tiếng Việt, tên dài, bán theo cân. */
const SAMPLE: Order = {
  id: 'sample',
  code: 'IN-THU-0001',
  tableId: null,
  tableName: 'Trệt · Bàn 3',
  status: 'Paid',
  openedAt: new Date().toISOString(),
  paidAt: new Date().toISOString(),
  subtotal: 87_000,
  discount: 0,
  total: 87_000,
  paymentMethod: 'Cash',
  amountGiven: 100_000,
  changeGiven: 13_000,
  customerName: null,
  note: null,
  deviceId: 'THU',
  clientCreatedAt: new Date().toISOString(),
  voidedAt: null,
  voidReason: null,
  synced: 1,
  items: [
    { id: '1', productId: null, name: 'Cà phê sữa', unitPrice: 22_000, qtyMilli: 2000, lineTotal: 44_000, optionLabel: 'Đá', note: null },
    { id: '2', productId: null, name: 'Bánh mì thịt nướng đặc biệt', unitPrice: 25_000, qtyMilli: 1000, lineTotal: 25_000, optionLabel: null, note: 'ít ớt' },
    { id: '3', productId: null, name: 'Thịt ba rọi', unitPrice: 160_000, qtyMilli: 112, lineTotal: 18_000, optionLabel: null, note: null },
  ],
}

export function PrinterSettingsScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  const [agent, setAgent] = useState<AgentInfo | null | 'checking'>('checking')
  const [cfg, setCfg] = useState<PrinterConfig>(defaultPrinter)
  const [found, setFound] = useState<{ windows: string[]; serial: string[] }>({ windows: [], serial: [] })
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      const saved = await kvGet<PrinterConfig>('printer')
      if (saved) setCfg(saved)

      const info = await probeAgent()
      setAgent(info)
      if (info) {
        try {
          setFound(await listPrinters())
        } catch {
          /* không đọc được danh sách thì vẫn gõ tay được địa chỉ */
        }
      }
    })()
  }, [])

  // Ảnh xem trước do agent tạo ra là blob URL — không thu hồi thì rò bộ nhớ.
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const patch = (p: Partial<PrinterConfig>) => setCfg((c) => ({ ...c, ...p }))

  async function save() {
    await kvSet('printer', cfg)
    say('Đã lưu máy in')
  }

  async function testPreview() {
    try {
      const url = await previewReceipt(SAMPLE, shop, cfg)
      if (preview) URL.revokeObjectURL(preview)
      setPreview(url)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Không xem trước được')
    }
  }

  async function testPrint() {
    if (!cfg.address.trim()) {
      say('Chưa chọn máy in')
      return
    }
    await kvSet('printer', cfg)
    try {
      await printReceipt(SAMPLE, shop, cfg)
      say('Đã gửi tờ bill thử ra máy in')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Không in được')
    }
  }

  if (agent === 'checking') return <div className="screen" />

  if (!agent) {
    return (
      <div className="screen">
        <AppBar title="Máy in bill" onBack={() => nav('/settings')} />
        <div className="body">
          <div className="card warn-card">
            <p>
              Máy này chưa chạy <b>sigoPOS Print Agent</b> — chương trình nhỏ nối app với máy in.
              Cài và mở nó lên rồi quay lại màn hình này.
            </p>
          </div>
          <p className="note">
            Không có nó thì mọi thứ khác vẫn bán bình thường, chỉ là không in được tờ bill giấy.
          </p>
          <button type="button" className="btn btn-ghost wide" onClick={() => window.location.reload()}>
            Đã mở rồi, kiểm tra lại
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      <AppBar title="Máy in bill" sub={`Đã nối agent trên ${agent.machine}`} onBack={() => nav('/settings')} />

      <div className="body">
        <div className="field">
          <span>Máy in nối kiểu gì</span>
          <div className="seg seg-3">
            {(
              [
                ['Network', 'Mạng LAN'],
                ['Windows', 'Cắm USB'],
                ['Serial', 'Cổng COM'],
              ] as [PrinterKind, string][]
            ).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                aria-pressed={cfg.kind === kind}
                onClick={() => patch({ kind, address: '' })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {cfg.kind === 'Network' && (
          <>
            <label className="field">
              <span>Địa chỉ IP của máy in</span>
              <input
                inputMode="decimal"
                value={cfg.address}
                onChange={(e) => patch({ address: e.target.value })}
                placeholder="192.168.1.50"
              />
            </label>
            <p className="note">
              In một trang tự kiểm tra từ máy in (thường là giữ nút Feed lúc bật máy) — địa chỉ IP
              nằm trên tờ giấy đó.
            </p>
          </>
        )}

        {cfg.kind === 'Windows' && (
          <label className="field">
            <span>Chọn máy in đã cài trong Windows</span>
            <select value={cfg.address} onChange={(e) => patch({ address: e.target.value })}>
              <option value="">— Chọn máy in —</option>
              {found.windows.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        )}

        {cfg.kind === 'Serial' && (
          <div className="field-row">
            <label className="field">
              <span>Cổng COM</span>
              <select value={cfg.address} onChange={(e) => patch({ address: e.target.value })}>
                <option value="">— Chọn —</option>
                {found.serial.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Tốc độ</span>
              <select value={cfg.baudRate} onChange={(e) => patch({ baudRate: Number(e.target.value) })}>
                {[9600, 19200, 38400, 57600, 115200].map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <div className="field">
          <span>Khổ giấy</span>
          <div className="seg">
            {([58, 80] as const).map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={cfg.paperWidthMm === w}
                onClick={() => patch({ paperWidthMm: w })}
              >
                {w}mm
              </button>
            ))}
          </div>
        </div>

        <label className="row-btn">
          <span className="row-icon" aria-hidden="true">✂️</span>
          <span className="row-text">
            <b>Tự cắt giấy sau khi in</b>
            <small>Máy in không có dao cắt thì tắt đi</small>
          </span>
          <input type="checkbox" checked={cfg.cut} onChange={(e) => patch({ cut: e.target.checked })} />
        </label>

        <label className="row-btn">
          <span className="row-icon" aria-hidden="true">💰</span>
          <span className="row-text">
            <b>Mở két tiền khi in</b>
            <small>Két nối vào máy in bằng dây RJ11</small>
          </span>
          <input
            type="checkbox"
            checked={cfg.openDrawer}
            onChange={(e) => patch({ openDrawer: e.target.checked })}
          />
        </label>

        {preview && (
          <div className="card">
            <h2 className="section-label">Tờ bill sẽ ra như thế này</h2>
            <img className="bill-preview" src={preview} alt="Xem trước hoá đơn" />
          </div>
        )}

        <div className="sheet-actions">
          <button type="button" className="btn btn-ghost grow" onClick={testPreview}>
            Xem trước
          </button>
          <button type="button" className="btn btn-ghost grow" onClick={testPrint}>
            In thử
          </button>
        </div>

        {cfg.openDrawer && (
          <button
            type="button"
            className="btn btn-ghost wide"
            onClick={async () => {
              try {
                await openDrawer(cfg)
                say('Đã gửi lệnh mở két')
              } catch (e) {
                say(e instanceof Error ? e.message : 'Không mở được két')
              }
            }}
          >
            Thử mở két
          </button>
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" onClick={save}>
          Lưu
        </button>
      </div>
    </div>
  )
}
